/* eslint-disable @reatom/unit-naming-rule */
import { useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { reatomCheckCodeAvailability } from '@root/features/referral/atoms/reatom-check-code-availability'
import { PaymentWallets } from '@root/features/wallet/payment-wallets/ui/payment-wallets'
import { Input } from '@root/shared/ui-kit'

import type { DeviceType } from '@blade/ui-kit'
import type { ReferralCode } from '@root/entities/referral'
import type { WalletModel } from '@root/entities/wallet'

interface ReferralCodeFormValues {
  wallet: WalletModel | undefined
  referralCode: ReferralCode | undefined
}

export interface ReferralCodeSubmitValues {
  wallet: WalletModel
  referralCode: ReferralCode
}

interface ReferralCodeFormProps {
  onCancel?: () => void
  onSubmit: (values: Required<ReferralCodeSubmitValues>) => void
  existingReferralCode?: ReferralCode
  state: AsyncStateN.AsyncStateN<unknown, unknown>
  device: DeviceType
  className?: string
}

export const ReferralCodeForm = (props: ReferralCodeFormProps) => {
  const { existingReferralCode, state, device, className } = props
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)

  const availabilityModel = useMemo(
    () =>
      reatomCheckCodeAvailability({
        currentReferralCode: existingReferralCode,
      }),
    [existingReferralCode],
  )
  const [checkAvailabilityRequestState] = useAtom(
    availabilityModel.checkAvailabilityState,
  )
  const [_, setReferralCode] = useAtom(availabilityModel.referralCodeAtom)

  const form = useForm<ReferralCodeFormValues>({
    mode: 'all',
    defaultValues: {
      wallet: undefined,
      referralCode: existingReferralCode,
    },
  })

  const watchedReferralCode = form.watch('referralCode')

  useEffect(() => {
    // TODO: Check why form values are undefined during submit?
    setReferralCode(watchedReferralCode)
  }, [setReferralCode, watchedReferralCode])

  useEffect(() => {
    AsyncStateN.matchWaiting(checkAvailabilityRequestState, {
      Success: (x) => {
        if (x.available) {
          form.clearErrors('referralCode')
        } else {
          form.setError('referralCode', {
            message: t('error.referral.referral-code-must-be-unique'),
          })
        }
      },
      Error: () => {
        form.setError('referralCode', {
          message: t('common.something-went-wrong'),
        })
      },
      Waiting: () => {},
    })
  }, [checkAvailabilityRequestState, form, t])

  const handleSubmit_ = form.handleSubmit(async (data) => {
    if (!data.wallet || data.referralCode === undefined) {
      return
    }
    props.onSubmit({
      wallet: data.wallet,
      referralCode: data.referralCode,
    })
  })

  const disabled =
    !form.formState.isValid ||
    checkAvailabilityRequestState.value?.available !== true ||
    (existingReferralCode
      ? existingReferralCode === watchedReferralCode
      : false)

  return (
    <Root className={className} onSubmit={handleSubmit_}>
      <Controller
        name="wallet"
        control={form.control}
        rules={{ required: t('validation.required') }}
        disabled={state.loading}
        render={({ field, fieldState }) => (
          <PaymentWallets
            css={css`
              --payment-wallets-input-height: ${device === 'desktop'
                ? '36px'
                : '41px'};
            `}
            disabled={field.disabled}
            device={device}
            paymentSource={
              field.value
                ? { type: 'internal-wallet', wallet: field.value }
                : undefined
            }
            errorMessage={fieldState.error?.message}
            setPaymentSource={(x) => {
              field.onChange(x.wallet)
              form.trigger('wallet')
            }}
          />
        )}
      />
      <Controller
        name="referralCode"
        control={form.control}
        disabled={state.loading}
        rules={{
          required: t('validation.required'),
          maxLength: {
            message: t('validation.max_length', { length: 32 }),
            value: 32,
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            startContent={t('referral.referral_code')}
            placeholder={t('referral.enter_unique_code')}
            error={fieldState.error?.message}
            size={device === 'desktop' ? 'h36' : 'm'}
          />
        )}
      />
      <Buttons>
        {existingReferralCode ? undefined : (
          <Note>
            <Trans
              i18nKey="referral.referral-code-form.note"
              values={{ network: Network.label(network) }}
              components={[<strong key={0} />]}
            />
          </Note>
        )}
        <Button
          disabled={disabled}
          busy={state.loading || checkAvailabilityRequestState.loading}
          fullWidth
          radius={'rounded'}
          color="primary"
          size={device === 'desktop' ? 'h36' : 'm'}
          type="submit"
        >
          {t(
            existingReferralCode
              ? 'referral.update_referral_code'
              : 'referral.create_referral_code',
          )}
        </Button>
      </Buttons>
    </Root>
  )
}

const Root = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const Buttons = styled.div``

const Note = styled.p`
  color: var(--textA-6);
  text-align: center;

  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;

  margin-bottom: 12px;

  strong {
    color: var(--textA-11);
    font-weight: 400;
  }
`
